import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2, Sliders, GripVertical } from 'lucide-react';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../config/settings-api';
import {
  DEFAULT_ORDER_LIST_COLUMNS,
  getOrderListColumnLabel,
  migrateOrderListColumnsForRegion,
  filterOrderListColumnsForRegion,
  normalizeOrderListColumns,
} from '../../utils/orderConfigHelpers';
import './Settings.css';
import './OrderSettings.css';

const PROTECTED_KEYS = new Set(['status', 'orderId']);

const serializeColumns = (columns) =>
  JSON.stringify(
    (columns || []).map((col) => ({
      key: col.key,
      visible: !!col.visible,
      position: col.position,
    }))
  );

const getVisibleRows = (rows) =>
  [...rows].filter((row) => row.visible).sort((a, b) => a.position - b.position);

const rebuildRows = (visibleKeys, allRows) => {
  const byKey = new Map(allRows.map((row) => [row.key, row]));
  const next = [];

  visibleKeys.forEach((key, index) => {
    const row = byKey.get(key);
    if (!row) return;
    next.push({ ...row, visible: true, position: index });
  });

  let position = visibleKeys.length;
  allRows.forEach((row) => {
    if (visibleKeys.includes(row.key)) return;
    next.push({ ...row, visible: false, position });
    position += 1;
  });

  return next.sort((a, b) => a.position - b.position);
};

const mergeCustomFieldColumns = (rows, customFields = []) => {
  const list = [...rows];
  const keys = new Set(list.map((row) => row.key));
  customFields.forEach((field) => {
    const key = String(field?.key || '').trim();
    if (!key || keys.has(key)) return;
    list.push({
      key,
      label: String(field.label || key).trim() || key,
      visible: false,
      position: list.length,
    });
    keys.add(key);
  });
  return list;
};

const OrderListColumnsSettings = ({
  columns = [],
  customFields = [],
  canManage,
  isIndia = false,
  onSaved,
}) => {
  const [rows, setRows] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const columnsStr = JSON.stringify(columns);
  const customFieldsStr = JSON.stringify(customFields);

  useEffect(() => {
    const normalized = migrateOrderListColumnsForRegion(
      mergeCustomFieldColumns(
        normalizeOrderListColumns(columns?.length ? columns : DEFAULT_ORDER_LIST_COLUMNS),
        customFields
      ),
      isIndia
    );
    setRows(normalized);
    setInitialSnapshot(serializeColumns(normalized));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsStr, customFieldsStr, isIndia]);

  const visibleRows = useMemo(() => getVisibleRows(rows), [rows]);
  const hiddenRows = useMemo(
    () => rows.filter((row) => !row.visible).sort((a, b) => a.label.localeCompare(b.label)),
    [rows]
  );

  const isChanged = serializeColumns(rows) !== initialSnapshot;

  const getLabel = (row) => getOrderListColumnLabel(row, isIndia);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const keys = visibleRows.map((row) => row.key);
    const [removed] = keys.splice(draggedIndex, 1);
    keys.splice(index, 0, removed);
    setRows(rebuildRows(keys, rows));
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleRemoveColumn = (key) => {
    if (PROTECTED_KEYS.has(key)) return;
    const keys = visibleRows.map((row) => row.key).filter((k) => k !== key);
    setRows(rebuildRows(keys, rows));
  };

  const handleAddColumn = (key) => {
    const keys = [...visibleRows.map((row) => row.key), key];
    setRows(rebuildRows(keys, rows));
    setShowAddModal(false);
  };

  const handleSave = async () => {
    const visibleCount = rows.filter((row) => row.visible).length;
    if (!visibleCount) {
      toast.error('At least one column must be visible');
      return;
    }

    setSaving(true);
    try {
      const payload = filterOrderListColumnsForRegion(rows, isIndia).map((row, index) => ({
        key: row.key,
        label: row.label,
        visible: !!row.visible,
        position: index,
      }));

      const result = await settingsAPI.updateOrderListColumns(payload);
      if (result.success) {
        toast.success('Order column layout saved successfully');
        setInitialSnapshot(serializeColumns(payload));
        onSaved?.(payload);
      } else {
        toast.error(result.msg || result.error || 'Failed to save layout');
      }
    } catch {
      toast.error('Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-card order-settings-card">
      <div className="settings-card-header">
        <div className="settings-card-title">
          <Sliders size={20} />
          <div>
            <h2>Order Columns Layout</h2>
            <p>Drag to reorder, remove columns, or add Order Buttons and more</p>
          </div>
        </div>
        {canManage && (
          <button type="button" className="settings-save-btn" onClick={handleSave} disabled={!isChanged || saving}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>

      <div className="order-settings-body">
        {visibleRows.length === 0 ? (
          <p className="order-settings-empty">No columns selected. Click Add Column below.</p>
        ) : (
          <div className="order-columns-drag-list">
            {visibleRows.map((row, index) => (
              <div
                key={row.key}
                className={`order-column-drag-card ${draggedIndex === index ? 'dragging' : ''}`}
                draggable={canManage && !saving}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
              >
                <GripVertical size={16} className="order-column-grip-icon" />
                <span className="order-column-label">{getLabel(row)}</span>
                {canManage && !PROTECTED_KEYS.has(row.key) && (
                  <button
                    type="button"
                    className="order-column-remove-btn"
                    disabled={saving}
                    onClick={() => handleRemoveColumn(row.key)}
                    title="Remove column"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {canManage && (
          <div className="order-settings-add-row">
            <button
              type="button"
              className="settings-inline-btn"
              onClick={() => setShowAddModal(true)}
              disabled={saving || hiddenRows.length === 0}
            >
              <Plus size={16} /> Add Column
            </button>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="colsettings-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="colsettings-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="colsettings-modal-header">
              <h2>Add Column</h2>
              <button type="button" className="colsettings-modal-close" onClick={() => setShowAddModal(false)}>
                &times;
              </button>
            </div>
            <div className="colsettings-modal-body">
              {hiddenRows.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#64748b' }}>All columns are currently added.</p>
              ) : (
                <div className="order-columns-add-options">
                  {hiddenRows.map((row) => (
                    <button
                      key={row.key}
                      type="button"
                      className="order-columns-option-btn"
                      onClick={() => handleAddColumn(row.key)}
                    >
                      <Plus size={15} className="order-columns-option-icon" />
                      <span>{getLabel(row)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default OrderListColumnsSettings;
